import { proxy } from 'valtio'
import { AnswerStatus, AnswerStatusStore } from '../answerStatusDrawer.tsx'
import {
  TestQuestion,
  TestValidateRequest,
  UserTestsApi,
} from '../../../shared/api/userTestsApi.ts'
import {
  Payload,
  ICurrentAnswer,
  shuffleArray,
} from '../../../shared/lib/utils.ts'

interface State {
  questions: readonly TestQuestion[]
  currentIndex: number
  currentAnswer: { index: number; answer: string } | null
  answerStatus?: AnswerStatus
  correctAnswersCount: number
  userAnswers: ICurrentAnswer[]
}

export class TestQuestionsExerciseStore {
  state: State

  constructor(
    questions: readonly TestQuestion[],
    private answerStore: AnswerStatusStore,
    private onComplete: (
      result: string,
      correctAnswersCount: number,
      payload: Payload[],
    ) => void,
    private userTestStore?: {
      state: { currentIndex: number; chatId: number }
    },
    private userTestsApi?: UserTestsApi,
  ) {
    const processedQuestions = questions.map((q) => {
      const correctAnswer = q.options[0]
      const shuffledOptions = shuffleArray([...q.options])
      return {
        ...q,
        options: shuffledOptions,
        correctAnswer,
      }
    })

    this.state = proxy<State>({
      questions: processedQuestions,
      currentIndex: 0,
      currentAnswer: null,
      correctAnswersCount: 0,
      userAnswers: [],
    })
  }

  async selectAnswer(optionIndex: number, answer: string) {
    if (this.state.answerStatus) {
      return
    }

    this.state.currentAnswer = { index: optionIndex, answer }

    const checkResult = await this.checkAnswer(answer)
    const isCorrect = checkResult.isCorrect
    const correctAnswer = checkResult.correctAnswer

    if (isCorrect) {
      this.state.correctAnswersCount++
    }

    this.state.userAnswers.push({ index: this.state.currentIndex, isCorrect })

    const status = isCorrect
      ? AnswerStatus.correct()
      : AnswerStatus.wrong({
          description: 'Answer:',
          messageValue: correctAnswer || 'Unknown',
        })
    this.state.answerStatus = status
    this.answerStore.setStatus(status, () => {
      this.nextQuestion()
    })
  }

  async nextQuestion(): Promise<void> {
    if (this.state.currentIndex < this.state.questions.length - 1) {
      this.state.currentIndex += 1
      if (this.userTestStore) {
        this.userTestStore.state.currentIndex = this.state.currentIndex + 1
      }
      setTimeout(() => {
        this.state.answerStatus = undefined
        this.state.currentAnswer = null
      }, 50)
    } else {
      await this.completeTest()
    }
  }

  async completeTest(): Promise<void> {
    if (!this.userTestsApi || !this.userTestStore) {
      return
    }

    try {
      const validateData: TestValidateRequest[] = this.state.questions.map(
        (q, i) => ({
          question: q.question,
          category: q.category,
          correct: this.state.userAnswers[i]?.isCorrect ?? false,
        }),
      )

      const response = await this.userTestsApi.validateTest(
        this.userTestStore.state.chatId,
        validateData,
      )

      if (response.status === 'success') {
        this.state.answerStatus = AnswerStatus.complete()
        const payload: Payload[] = this.state.questions.map((q, i) => ({
          item: q.question,
          correct: this.state.userAnswers[i]?.isCorrect ?? false,
        }))
        this.onComplete(
          'Test completed',
          this.state.correctAnswersCount,
          payload,
        )
      }
    } catch (error) {
      this.state.answerStatus = AnswerStatus.complete()
      const payload: Payload[] = this.state.questions.map((q, i) => ({
        item: q.question,
        correct: this.state.userAnswers[i]?.isCorrect ?? false,
      }))
      this.onComplete('Test completed', this.state.correctAnswersCount, payload)
    }
  }

  async checkAnswer(answer: string): Promise<{
    isCorrect: boolean
    correctAnswer: string | null
  }> {
    const currentQuestion = this.state.questions[this.state.currentIndex]
    if (!currentQuestion) {
      return { isCorrect: false, correctAnswer: null }
    }

    const correctAnswer = currentQuestion.correctAnswer || null

    if (!correctAnswer) {
      return { isCorrect: false, correctAnswer: null }
    }

    return {
      isCorrect: answer === correctAnswer,
      correctAnswer,
    }
  }

  updateQuestions(questions: readonly TestQuestion[]) {
    this.state.questions = questions
    this.state.currentIndex = 0
    this.state.currentAnswer = null
    this.state.answerStatus = undefined
    this.state.correctAnswersCount = 0
    this.state.userAnswers = []
  }
}
